import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { MessageIcon } from '../../../components/shared-components/message-icon';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, ZoomControl } from 'react-leaflet';
import { useMap } from 'react-leaflet/hooks';
import 'leaflet/dist/leaflet.css';
import { ConfigContext } from '../../../contexts/config-context';
import { LikeButton } from '../../main-page/components/featured-listings';
import { formatNumber } from '../../main-page/home-utils';
import { listingPhoto } from './result-card';

const DEFAULT_CENTER = [44.8125, 20.4612];
const DEFAULT_ZOOM = 12;
const MAX_FIT_ZOOM = 14;
const POPUP_WIDTH = 252;
const TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
// Esri has no tiles of this style past this zoom; closer in, the last level is stretched
const TILES_MAX_ZOOM = 16;
// floors without a number, shortened the way listings usually write them
const FLOOR_SHORT = { basement_level: 'SU', lower_ground: 'PS', ground: 'PR', higher_ground: 'VPR' };

const shortPrice = (price) => price >= 1000 ? `${Math.round(price / 1000)}k €` : price > 0 ? `${formatNumber(price)} €` : '•';

const FitToPins = ({ points, layoutKey }) => {
    const map = useMap();

    useEffect(() => {
        // the container changes size when the view is switched
        map.invalidateSize();
        if (points.length > 0) {
            map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: MAX_FIT_ZOOM });
        }
    }, [map, points, layoutKey]);

    return null;
};

const Spec = ({ title, value, children }) => (
    <span className='hs-map-card-spec' title={title}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
        <span>{value}</span>
    </span>
);

const PopupCard = ({ listing, onMessage, onHide }) => {
    const { api, num_rooms_options, num_bathrooms_options, floor_options } = useContext(ConfigContext);
    const { t } = useTranslation(["results", "listing"]);
    const navigate = useNavigate();
    const [photo, setPhoto] = useState(0);

    const total = listing.photos?.length || 0;
    const price = parseFloat(listing.price) || 0;
    const rooms = num_rooms_options.find(o => o.id === listing.num_rooms)?.label;
    const baths = num_bathrooms_options.find(o => o.id === listing.num_bathrooms)?.label;
    const floorLabel = floor_options.find(o => o.id === listing.floor)?.label;
    const floor = FLOOR_SHORT[floorLabel] || floorLabel;
    const open = () => navigate(`/oglas/${listing.id}`);
    const step = (dir) => (e) => {
        e.stopPropagation();
        setPhoto((photo + dir + total) % total);
    };

    return (
        <div className='hs hs-map-card'>
            <div className='hs-map-card-photo' style={{ backgroundImage: `url(${listingPhoto(listing, api, photo)})` }} onClick={open}>
                {total > 1 && <>
                    <button className='hs-map-card-arrow prev' onClick={step(-1)} aria-label={t('prev_photo')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                    </button>
                    <button className='hs-map-card-arrow next' onClick={step(1)} aria-label={t('next_photo')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                    </button>
                </>}
                {listing.promoted && <span className='hs-map-card-badge'>{listing.promoted_value?.label || 'Premium'}</span>}
                {total > 0 && <span className='hs-map-card-count'>{photo + 1}/{total}</span>}
            </div>
            <div className='hs-map-card-body' onClick={open}>
                <span className='hs-map-card-price'>{price > 0 ? `${formatNumber(price)} €` : t('price_placeholder', { ns: 'listing' })}</span>
                <div className='hs-map-card-specs'>
                    {rooms && <Spec title={t('num_rooms', { ns: 'listing' })} value={rooms}>
                        <path d="M4 21h16" /><path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17" /><path d="M14 12h.01" />
                    </Spec>}
                    <Spec title={t('area', { ns: 'listing' })} value={`${formatNumber(listing.area)} m²`}>
                        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M8 16 16 8M12 8h4v4M12 16H8v-4" />
                    </Spec>
                    {baths && <Spec title={t('num_bathrooms', { ns: 'listing' })} value={baths}>
                        <path d="M4 12h16v3a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-3Z" /><path d="M6 12V5a2 2 0 0 1 3.5-1.3" /><path d="M7 20l-1 2M17 20l1 2" />
                    </Spec>}
                    {floor && <Spec title={t('floor', { ns: 'listing' })} value={floor}>
                        <path d="M3 21h4v-4h4v-4h4V9h4V5h2" /><path d="M3 21V3" />
                    </Spec>}
                </div>
            </div>
            <div className='hs-map-card-actions'>
                <button className='hs-map-card-msg' onClick={() => onMessage(listing)}>
                    <MessageIcon />
                    {t('message')}
                </button>
                <span className='hs-spacer'></span>
                <button className='hs-map-card-hide' onClick={() => onHide(listing.id)} aria-label={t('hide_listing')} title={t('hide_listing')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.7 5.1A9.8 9.8 0 0 1 12 5c5 0 8.5 4.2 9.6 6.2.3.5.3 1.1 0 1.6-.5.9-1.4 2.2-2.7 3.4" /><path d="M6.6 6.6C4.4 8 3 10 2.4 11.2c-.3.5-.3 1.1 0 1.6C3.5 14.8 7 19 12 19c1.9 0 3.6-.6 5-1.5" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /><path d="m3 3 18 18" /></svg>
                </button>
                <LikeButton listing={listing.id} followers={listing.followers} className='hs-map-card-like' />
            </div>
        </div>
    );
};

const Pin = ({ listing, position, active, onHover, onMessage, onHide }) => {
    const markerRef = useRef(null);
    const label = shortPrice(parseFloat(listing.price) || 0);
    const promoted = !!listing.promoted;

    // the icon is only rebuilt when its content changes; replacing it on every
    // hover would swap the element under the cursor
    const icon = useMemo(() => L.divIcon({
        className: 'hs-pin-anchor',
        iconSize: [0, 0],
        html: `<span class="hs-pin ${promoted ? 'premium' : ''}">${label}</span>`,
    }), [label, promoted]);

    useEffect(() => {
        const pin = markerRef.current?.getElement()?.querySelector('.hs-pin');
        if (pin) pin.classList.toggle('active', active);
    }, [active, icon]);

    // an open popup pans the map back to itself whenever it gets new children, so the card
    // has to stay the same element while the map re-renders on hover
    const card = useMemo(() => <PopupCard listing={listing} onMessage={onMessage} onHide={onHide} />, [listing, onMessage, onHide]);

    return (
        <Marker ref={markerRef} position={position} icon={icon} zIndexOffset={active ? 1000 : promoted ? 500 : 0}
            eventHandlers={{ mouseover: () => onHover(listing.id), mouseout: () => onHover(null) }}>
            <Popup className='hs-map-popup' closeButton={false} offset={[0, -26]} minWidth={POPUP_WIDTH} maxWidth={POPUP_WIDTH}
                autoPanPadding={[12, 12]}>
                {card}
            </Popup>
        </Marker>
    );
};

export const SearchMap = ({ listings, hidden, activeId, onHover, onMessage, onHide, layoutKey }) => {
    const { t } = useTranslation(["results"]);

    // the map is only refitted when the search results themselves change; `listings` must keep
    // its identity between renders, otherwise every hover would undo the visitor's zoom
    const located = useMemo(() => listings
        .map(listing => ({ listing, position: [parseFloat(listing.lat), parseFloat(listing.lng)] }))
        .filter(pin => Number.isFinite(pin.position[0]) && Number.isFinite(pin.position[1])), [listings]);
    const points = useMemo(() => located.map(pin => pin.position), [located]);
    const pins = located.filter(pin => !hidden[pin.listing.id]);

    return (
        <div className='hs-map'>
            <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} zoomControl={false} scrollWheelZoom={true}>
                <TileLayer url={TILES} attribution='&copy; Esri' maxNativeZoom={TILES_MAX_ZOOM} />
                <ZoomControl position='topright' />
                <FitToPins points={points} layoutKey={layoutKey} />
                {pins.map(({ listing, position }) => (
                    <Pin key={`pin-${listing.id}`} listing={listing} position={position} active={listing.id === activeId}
                        onHover={onHover} onMessage={onMessage} onHide={onHide} />
                ))}
            </MapContainer>
            <div className='hs-map-count'>{t('on_map', { n: pins.length })}</div>
        </div>
    );
};
